import {HttpErrorResponse} from '@angular/common/http';
import {BugReportErrorContext} from '../../api/models/bug-report-error-context.model';

const MAX_TEXT_LENGTH = 500;
const MAX_PATH_LENGTH = 512;

function text(value: unknown, limit: number = MAX_TEXT_LENGTH): string | undefined {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return undefined;
  }
  const result = String(value).trim().slice(0, limit);
  return result || undefined;
}

function object(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function firstText(...values: unknown[]): string | undefined {
  for (const value of values) {
    const result = text(value);
    if (result) {
      return result;
    }
  }
  return undefined;
}

function pathOnly(url: string | null | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  try {
    const path = new URL(url, 'https://placeholder.invalid').pathname;
    // Omit unusually long path segments, which commonly carry one-time tokens.
    return path.split('/').map(segment => segment.length > 64 ? '[redacted]' : segment).join('/').slice(0, MAX_PATH_LENGTH);
  } catch {
    return undefined;
  }
}

function identifier(
  body: Record<string, unknown>,
  metadata: Record<string, unknown>,
  meta: Record<string, unknown>,
  camelCase: string,
  snakeCase: string,
  header: string | null
): string | undefined {
  return firstText(
    body[camelCase], body[snakeCase],
    metadata[camelCase], metadata[snakeCase],
    meta[camelCase], meta[snakeCase], header
  );
}

export function buildBugReportErrorContext(
  response: HttpErrorResponse,
  method?: string,
  pageUrl?: string
): BugReportErrorContext {
  const body = object(response.error);
  const nestedError = object(body['error']);
  const metadata = object(body['metadata']);
  const meta = object(body['meta']);
  const violations = Array.isArray(body['violations']) ? object(body['violations'][0]) : {};
  const errors = Array.isArray(body['errors']) ? object(body['errors'][0]) : {};
  const requestMethod = text(method, 16);
  const requestPath = pathOnly(response.url);
  const pagePath = pathOnly(pageUrl);
  const responseCode = text(body['code'], 64);
  const responseTitle = text(body['title']);
  const responseMessage = firstText(
    violations['message'], errors['message'], body['hydra:description'],
    body['detail'], body['message'], body['error_description'],
    nestedError['message'], nestedError['detail']
  );
  const responseType = text(body['type']);
  const requestId = identifier(body, metadata, meta, 'requestId', 'request_id', response.headers.get('x-request-id'));
  const traceId = identifier(body, metadata, meta, 'traceId', 'trace_id', response.headers.get('x-trace-id'));
  const correlationId = identifier(body, metadata, meta, 'correlationId', 'correlation_id', response.headers.get('x-correlation-id'));

  return {
    occurredAt: new Date().toISOString(),
    status: response.status,
    ...(requestMethod && {method: requestMethod}),
    ...(requestPath && {requestPath}),
    ...(pagePath && {pagePath}),
    ...(responseCode && {responseCode}),
    ...(responseTitle && {responseTitle}),
    ...(responseMessage && {responseMessage}),
    ...(responseType && {responseType}),
    ...(requestId && {requestId}),
    ...(traceId && {traceId}),
    ...(correlationId && {correlationId})
  };
}
